// Copyright 2018-2024 the Deno authors. All rights reserved. MIT license.
// This module is browser compatible.

import type { Range } from "./types.ts";
import { parseRange } from "./parse_range.ts";

/**
 * Parses the given range string and returns a Range object. If the range string
 * is invalid, `undefined` is returned.
 *
 * @example Usage
 * ```ts
 * import { tryParseRange } from "@std/semver";
 * import { assertEquals } from "@std/assert";
 *
 * assertEquals(tryParseRange(">=1.2.3 <1.2.4"), [
 *  [
 *    { operator: ">=", major: 1, minor: 2, patch: 3, prerelease: [], build: [] },
 *    { operator: "<", major: 1, minor: 2, patch: 4, prerelease: [], build: [] },
 *  ],
 * ]);
 * ```
 *
 * @param value The range string
 * @returns A Range object if valid otherwise `undefined`
 */
export function tryParseRange(value: string): Range | undefined {
  try {
    // Return '*' instead of '' so that truthiness works.
    // This will throw if it's invalid anyway
    return parseRange(value);
  } catch {
    return undefined;
  }
}

// denoCacheMetadata={"headers":{"content-security-policy":"default-src 'none'; script-src 'none'; style-src 'none'; img-src 'none'; font-src 'none'; connect-src 'none'; frame-src 'none'; object-src 'none'; frame-ancestors 'none'; sandbox; form-action 'none';","x-jsr-cache-id":"IAD","access-control-allow-origin":"*","accept-ranges":"bytes","expires":"Sat, 08 Nov 2025 09:36:15 GMT","x-goog-generation":"1725446501950144","access-control-expose-headers":"*","x-content-type-options":"nosniff","x-goog-stored-content-encoding":"identity","x-goog-metageneration":"1","x-goog-storage-class":"STANDARD","content-length":"1045","content-type":"text/typescript","x-robots-tag":"noindex","via":"1.1 google","cross-origin-resource-policy":"cross-origin","alt-svc":"h3=\":443\"; ma=2592000,h3-29=\":443\"; ma=2592000","x-guploader-uploadid":"AHmUCY2Esm_FD7XRgJXnHn4hO9YI5TTTGPdTJb_-qTz42QjwkSy_bI7q0pKrm-mQLKmJRYhGdQ8","date":"Fri, 08 Nov 2024 09:36:15 GMT","etag":"\"e75b32d7125c9b5daa839dcb5fae513d\"","x-goog-stored-content-length":"1045","age":"2026","x-goog-hash":"crc32c=YhjlwQ==,md5=51sy1xJcm12qg53LX65RPQ==","x-jsr-cache-status":"hit","server":"UploadServer","last-modified":"Wed, 04 Sep 2024 10:41:41 GMT","cache-control":"public, max-age=31536000, immutable"},"url":"https://jsr.io/@std/semver/1.0.3/try_parse_range.ts","time":1731060601}